import { Domain } from '@wix/ambassador-meta-site-search-web/types';
import { Box, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { Confirm as ConfirmIcon } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import Image from 'next/image';

import { TOOLTIP_ENTER_DELAY } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import { SiteMetaData } from '../../../../types';

import css from './index.module.scss';

export type SiteSelectorListItemProps = {
    site: SiteMetaData;
    currentSiteId?: string | null;
    withScroll?: boolean;
    onSiteItemClick: (site: SiteMetaData) => void;
};

export const siteThumbnail = (id: string) => `https://manage.wix.com/site-thumbnail/${id}?preset=site-list`;

const getDomainName = (domains?: Domain[]): string | undefined =>
    domains ? domains.find((domain) => domain.primary)?.name || domains[0]?.name : undefined;

export const SiteSelectorListItem =
    ({ site, withScroll, currentSiteId, onSiteItemClick }: SiteSelectorListItemProps) => {
        const { t } = useTranslation();

        const onKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                onSiteItemClick(site);
            }
        };
        const domainName = getDomainName(site.domains);
        return (
            <div
                key={site.metaSiteId}
                onClick={() => onSiteItemClick(site)}
                data-hook={DATA_HOOKS.SITE_SELECTOR_LIST_ITEM}
                className={classNames(css.SiteSelectorItem, { [css.withScroll]: withScroll })}
                aria-label={site.siteProperties?.siteDisplayName}
                tabIndex={0}
                onKeyDown={onKeyDown}
            >
                <Box
                    verticalAlign="middle"
                    align="space-between"
                    className={classNames(css.SiteSelectorItemContent,
                        { [css.selected]: currentSiteId === site.metaSiteId })}
                >
                    <Box verticalAlign="middle" overflow="hidden">
                        <Image src={siteThumbnail(site.metaSiteId)} className={css.siteImage}
                            alt={site.name} width={60} height={36} />
                        <Box direction="vertical" overflow="hidden">
                            <Text
                                className={css.siteName}
                                size='medium'
                                weight='thin'
                                ellipsis
                                tooltipProps={{ enterDelay: TOOLTIP_ENTER_DELAY }}
                                dataHook={DATA_HOOKS.SITE_SELECTOR_LIST_ITEM_NAME}
                            >
                                {site.siteProperties?.siteDisplayName}
                            </Text>
                            {domainName && <Text className={css.domainName} size="tiny" weight="thin" ellipsis>{domainName}</Text>}
                            {site.premiumAssigned && <Text className={css.premiumPlan} size="tiny" weight="thin">{t('site-selector.premium.plan')}
                                <Text dataHook={DATA_HOOKS.SITE_SELECTOR_LIST_ITEM_PREMIUM_BADGE} className={css.premiumPlanText} size="tiny" weight="thin">
                                    {t('site-selector-modal.premium-ribbon.label')}
                                </Text>
                            </Text>}
                        </Box>
                    </Box>

                    <Box verticalAlign="middle" gap={2}>
                        <Box height={24} width={24}>
                            {currentSiteId === site.metaSiteId && (
                                <Box
                                    dataHook={DATA_HOOKS.SITE_SELECTOR_LIST_ITEM_CONFIRM_ICON}
                                    className={css.confirmIcon}
                                >
                                    <ConfirmIcon />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </div>
        );
    };
